import { CurrencyAmount, Token } from '@uniswap/sdk-core'
import { getUniswapWagmiPublicClient } from 'uniswap/src/features/providers/wagmiBridge'
import {
  DAILY_EPOCH_GAUGE_ADDRESS,
  INCENTIVE_GAUGE_ADDRESS,
} from 'uniswap/src/features/transactions/liquidity/steps/shared'
import {
  ClaimIncentiveRequest,
  StakeAfterClaimAccumulator,
  StakeAfterClaimConfig,
} from 'uniswap/src/features/transactions/liquidity/types'
import { TransactionStepType } from 'uniswap/src/features/transactions/steps/types'
import {
  ValidatedTransactionRequest,
  validateTransactionRequest,
} from 'uniswap/src/features/transactions/swap/utils/trade'
import { logger } from 'utilities/src/logger/logger'
import { encodeFunctionData } from 'viem'

export interface StakeBmxApprovalTransactionStep {
  type: TransactionStepType.StakeBmxApprovalTransaction
  config: StakeAfterClaimConfig
}

export interface StakeBmxTransactionStep {
  type: TransactionStepType.StakeBmxTransaction
  config: StakeAfterClaimConfig
}

const CLAIIED_EVENT_TOPIC_INCENTIVE = '0xf7a40077ff7a04c7e61f6f26fb13774259ddf1b6bce9ecf26a8276cdd3992683'
const CLAIMED_EVENT_TOPIC_DAILY = '0xd8138f8a3f377c5259ca548e70e4c2de94f129f5a11036a15b69513cba2b426a'
const ZERO_BIGINT = BigInt(0)

const ERC20_ABI = [
  {
    name: 'allowance',
    type: 'function',
    stateMutability: 'view',
    inputs: [
      { name: 'owner', type: 'address' },
      { name: 'spender', type: 'address' },
    ],
    outputs: [{ name: 'amount', type: 'uint256' }],
  },
  {
    name: 'approve',
    type: 'function',
    stateMutability: 'nonpayable',
    inputs: [
      { name: 'spender', type: 'address' },
      { name: 'amount', type: 'uint256' },
    ],
    outputs: [{ name: 'success', type: 'bool' }],
  },
] as const

export function createStakeAfterClaimAccumulator(): StakeAfterClaimAccumulator {
  return { totalClaimed: ZERO_BIGINT }
}

export function createStakeBmxApprovalStep(config: StakeAfterClaimConfig): StakeBmxApprovalTransactionStep {
  return {
    type: TransactionStepType.StakeBmxApprovalTransaction,
    config,
  }
}

export function createStakeBmxTransactionStep(config: StakeAfterClaimConfig): StakeBmxTransactionStep {
  return {
    type: TransactionStepType.StakeBmxTransaction,
    config,
  }
}

export async function accumulateClaimedBmx({
  chainId,
  hash,
  rewardTokenAddress,
  accumulator,
  gauge,
  bmxToken,
}: {
  chainId: number
  hash: string
  rewardTokenAddress?: `0x${string}`
  accumulator: StakeAfterClaimAccumulator
  gauge?: ClaimIncentiveRequest['gauge']
  bmxToken: Token
}): Promise<void> {
  const client = getUniswapWagmiPublicClient(chainId)
  if (!client) {
    return
  }

  const receipt = await client.waitForTransactionReceipt({ hash: hash as `0x${string}` })
  const parseDaily = !gauge || gauge === 'DailyEpochGauge'
  const parseIncentive = !gauge || gauge === 'IncentiveGauge'
  const dailyGaugeAddress = DAILY_EPOCH_GAUGE_ADDRESS.toLowerCase()
  const incentiveGaugeAddress = INCENTIVE_GAUGE_ADDRESS.toLowerCase()
  const bmxAddressLower = bmxToken.address.toLowerCase()
  const expectedRewardAddress = rewardTokenAddress?.toLowerCase()

  for (const log of receipt.logs ?? []) {
    if (typeof log.address !== 'string') {
      continue
    }
    const addressLower = log.address.toLowerCase()
    const isDailyAddress = parseDaily && addressLower === dailyGaugeAddress
    const isIncentiveAddress = parseIncentive && addressLower === incentiveGaugeAddress
    if (!isDailyAddress && !isIncentiveAddress) {
      continue
    }
    if (!Array.isArray(log.topics) || log.topics.length < 2) {
      if (!isDailyAddress) {
        continue
      }
    }
    const eventTopic = (log.topics[0] || '').toLowerCase()
    const isDailyClaim = isDailyAddress && eventTopic === CLAIMED_EVENT_TOPIC_DAILY
    const isIncentiveClaim = isIncentiveAddress && eventTopic === CLAIIED_EVENT_TOPIC_INCENTIVE
    if (!isDailyClaim && !isIncentiveClaim) {
      continue
    }

    let isBmxReward = false
    if (log.topics.length >= 3) {
      const tokenTopic = log.topics[2]
      if (typeof tokenTopic === 'string') {
        const tokenAddress = `0x${tokenTopic.slice(-40)}`.toLowerCase()
        if (tokenAddress === bmxAddressLower) {
          isBmxReward = true
        } else if (expectedRewardAddress && tokenAddress === expectedRewardAddress) {
          isBmxReward = true
        }
      }
    } else if (gauge === 'DailyEpochGauge') {
      // Daily gauge rewards BMX and may omit the reward token topic.
      isBmxReward = true
    }

    if (!isBmxReward) {
      continue
    }
    if (typeof log.data !== 'string') {
      continue
    }
    try {
      const amount = BigInt(log.data)
      accumulator.totalClaimed += amount
    } catch {
      continue
    }
  }
}

export async function buildStakeApprovalTxRequest(
  config: StakeAfterClaimConfig,
  amount: bigint,
): Promise<ValidatedTransactionRequest | undefined> {
  if (amount <= ZERO_BIGINT) {
    return undefined
  }
  const client = getUniswapWagmiPublicClient(config.chainId)
  if (!client) {
    return undefined
  }
  const allowance = (await client.readContract({
    address: config.bmxToken.address as `0x${string}`,
    abi: ERC20_ABI,
    functionName: 'allowance',
    args: [config.accountAddress, config.stakingSpenderAddress],
  })) as bigint

  if (allowance >= amount) {
    return undefined
  }

  const data = encodeFunctionData({
    abi: ERC20_ABI,
    functionName: 'approve',
    args: [config.stakingSpenderAddress, amount],
  }) as `0x${string}`

  return validateTransactionRequest({
    chainId: config.chainId,
    from: config.accountAddress,
    to: config.bmxToken.address as `0x${string}`,
    data,
    value: undefined,
  } as any)
}

export function buildStakeBmxTxRequest(
  config: StakeAfterClaimConfig,
  amount: bigint,
): ValidatedTransactionRequest | undefined {
  if (amount <= ZERO_BIGINT) {
    return undefined
  }

  const data = encodeFunctionData({
    abi: config.stakingRouterAbi,
    functionName: 'stakeBmx',
    args: [amount],
  }) as `0x${string}`

  const txRequest = validateTransactionRequest({
    chainId: config.chainId,
    from: config.accountAddress,
    to: config.stakingRouterAddress,
    data,
    value: undefined,
  } as any)

  if (!txRequest) {
    logger.warn('stakeAfterClaim', 'buildStakeBmxTxRequest', 'failed to construct stake tx', {
      chainId: config.chainId,
      accountAddress: config.accountAddress,
      stakingRouterAddress: config.stakingRouterAddress,
      amount: amount.toString(),
      estimatedAmount: config.estimatedClaimAmount?.toString?.(),
      data,
    })
    return undefined
  }

  logger.info('stakeAfterClaim', 'buildStakeBmxTxRequest', 'prepared stake tx', {
    chainId: config.chainId,
    accountAddress: config.accountAddress,
    stakingRouterAddress: config.stakingRouterAddress,
    amount: amount.toString(),
    estimatedAmount: config.estimatedClaimAmount?.toString?.(),
    data,
  })

  return txRequest
}

export function toBmxCurrencyAmount(token: Token, amount: bigint): CurrencyAmount<Token> {
  return CurrencyAmount.fromRawAmount(token, amount.toString())
}
